import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calculator, ArrowRight, Zap, ChevronRight, Bitcoin, 
  Feather as Ethereum, DollarSign, Cpu, TrendingUp 
} from 'lucide-react';
import { GradientBackground } from '../common/GradientBackground';
import { StatsRow } from '../stats/StatsRow';
import { useMinerstat } from '../../hooks/useMinerstat';
import { Loader } from '../common/Loader';

export function HomeHero() {
  const { miners, isLoading, error } = useMinerstat();

  return (
    <div className="relative min-h-screen bg-white overflow-hidden">
      <GradientBackground />
      
      <div className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="pt-20 pb-16 lg:pt-32 lg:pb-24">
            <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              {/* Left Column - Content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center lg:text-left"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 text-blue-600 mb-8">
                  <Zap className="w-4 h-4" />
                  <span className="text-sm font-medium">Real-time Mining Analytics</span>
                </div>

                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight mb-6">
                  The Future of
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                    Crypto Mining
                  </span>
                  Starts Here
                </h1>

                <p className="text-xl text-gray-600 mb-8 max-w-2xl lg:max-w-none">
                  Make data-driven decisions with real-time profitability analysis 
                  and comprehensive mining insights. Your all-in-one platform for 
                  maximizing mining efficiency.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Link
                    to="/#cost-settings"
                    className="group inline-flex items-center justify-center px-6 py-3 rounded-xl text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <Calculator className="w-5 h-5 mr-2" />
                    <span className="font-medium">Start Calculating</span>
                    <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>

                  <Link
                    to="/facilities"
                    className="group inline-flex items-center justify-center px-6 py-3 rounded-xl text-gray-700 bg-white border border-gray-200 hover:border-blue-500/20 hover:bg-blue-50/50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <span className="font-medium">Explore Hosting</span>
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>

                {/* Supported Coins */}
                <div className="mt-12">
                  <p className="text-sm text-gray-500 mb-4">Supported Cryptocurrencies</p>
                  <div className="flex items-center justify-center lg:justify-start gap-6">
                    <div className="flex items-center gap-2">
                      <Bitcoin className="w-6 h-6 text-orange-500" />
                      <span className="font-medium text-gray-900">Bitcoin</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Ethereum className="w-6 h-6 text-purple-500" />
                      <span className="font-medium text-gray-900">Ethereum</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-6 h-6 text-blue-500" />
                      <span className="font-medium text-gray-900">Litecoin</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Right Column - Interactive Illustration */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="hidden lg:block relative"
              >
                {/* Mining Rig Illustration */}
                <div className="relative w-full aspect-square">
                  {/* Background Grid */}
                  <div className="absolute inset-0 grid grid-cols-8 gap-4 opacity-10">
                    {[...Array(64)].map((_, i) => (
                      <div
                        key={i}
                        className="aspect-square rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 animate-pulse"
                        style={{ animationDelay: `${i * 0.1}s` }}
                      />
                    ))}
                  </div>

                  {/* Mining Hardware */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-4/5 aspect-video bg-white rounded-2xl shadow-2xl p-8 transform rotate-12">
                      {/* Cooling Fans */}
                      {[...Array(3)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute"
                          style={{ left: `${25 + i * 25}%`, top: '30%' }}
                          animate={{ rotate: 360 }}
                          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                        >
                          <div className="w-12 h-12 rounded-full border-4 border-blue-500/20 border-t-blue-500" />
                        </motion.div>
                      ))}

                      {/* LED Indicators */}
                      {[...Array(4)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute"
                          style={{ left: `${20 + i * 20}%`, bottom: '20%' }}
                          animate={{ opacity: [0.4, 1, 0.4] }}
                          transition={{ duration: 2, repeat: Infinity, delay: i * 0.5 }}
                        >
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Stats Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <StatsRow />
          </motion.div>

          {/* Available Miners Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="py-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Available Mining Hardware
              </h2>
              <p className="text-lg text-gray-600">
                Choose from our selection of high-performance ASIC miners
              </p>
            </div>

            {isLoading ? (
              <div className="flex justify-center">
                <Loader />
              </div>
            ) : error ? (
              <div className="text-center text-red-600">{error}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {miners.slice(0, 6).map((miner) => (
                  <Link
                    key={miner.model}
                    to={`/miner/${miner.model}`}
                    className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-blue-100"
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 bg-blue-50 rounded-xl group-hover:scale-110 transition-transform duration-300">
                        <Cpu className="w-6 h-6 text-blue-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{miner.name}</h3>
                        <p className="text-sm text-gray-500">{miner.manufacturer}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500 flex items-center gap-2">
                          <Zap className="w-4 h-4 text-yellow-500" />
                          Hashrate
                        </span>
                        <span className="font-medium text-gray-900">
                          {miner.hashrate} {miner.hashrateUnit}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-green-500" />
                          Efficiency
                        </span>
                        <span className="font-medium text-gray-900">
                          {(miner.hashrate / miner.power).toFixed(3)} {miner.hashrateUnit}/W
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <span className="px-3 py-1 text-sm font-medium bg-purple-50 text-purple-600 rounded-full">
                        {miner.algorithm}
                      </span>
                      <div className="flex items-center gap-2 text-blue-600 group-hover:translate-x-1 transition-transform">
                        <span className="text-sm font-medium">Details</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <div className="text-center mt-8">
              <Link
                to="/calculator"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors"
              >
                <span className="font-medium">View All Miners</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}